import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Settings Page
 * Personal settings and profile management for WebTrader
 */
import { useState, useEffect, useRef } from 'react';
import { useTraderAuth, traderAPI } from '../context/TraderAuthContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import TraderLayout from '../components/TraderLayout';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar as CalendarIcon,
  Lock, 
  Eye, 
  EyeOff,
  Save,
  Shield,
  Camera,
  Trash2,
  Loader2
} from 'lucide-react';

const TraderSettings = () => {
  const tx = useTraderText();
  const { user } = useTraderAuth();
  const { isDark } = useTraderTheme();
  const { t } = useTraderLanguage();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [avatarLoading, setAvatarLoading] = useState(false);
  
  // Avatar state
  const [avatar, setAvatar] = useState(null);
  
  // Profile form state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    city: '',
    country: '',
    postal_code: ''
  });
  
  // Password form state
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await traderAPI.get('/api/trader/me');
        const userData = response.data;
        setProfile({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          date_of_birth: userData.date_of_birth || '',
          address: userData.address || '',
          city: userData.city || '',
          country: userData.country || '',
          postal_code: userData.postal_code || ''
        });
        setAvatar(userData.avatar || null);
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error(tx(t('settings.fetchError')));
      } finally {
        setProfileLoading(false);
      }
    };
    
    fetchProfile();
  }, [t, tx]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { email, ...updateData } = profile; // Exclude email from update
      const response = await traderAPI.put('/api/trader/profile', updateData);
      
      if (response.data.success) {
        toast.success(tx(t('settings.profileUpdated')));
        // Update local storage user data
        const storedUser = JSON.parse(localStorage.getItem('trader_user') || '{}');
        const updatedUser = { ...storedUser, ...response.data.user };
        localStorage.setItem('trader_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      toast.error(tx(error.response?.data?.detail || t('settings.updateError')));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error(tx(t('settings.passwordMismatch')));
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error(tx(t('validation.passwordMin')));
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await traderAPI.put('/api/trader/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      
      if (response.data.success) {
        toast.success(tx(t('settings.passwordChanged')));
        setPasswords({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      }
    } catch (error) {
      toast.error(tx(error.response?.data?.detail || t('settings.passwordError')));
    } finally {
      setLoading(false);
    }
  };

  // Avatar upload handler
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(tx(t('settings.avatarInvalidType')));
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(tx(t('settings.avatarTooLarge')));
      return;
    }
    
    setAvatarLoading(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const response = await traderAPI.put('/api/trader/avatar', {
            avatar_data: reader.result,
            content_type: file.type
          });
          
          if (response.data.success) {
            setAvatar(response.data.avatar);
            toast.success(tx(t('settings.avatarUpdated')));
            
            // Update local storage
            const storedUser = JSON.parse(localStorage.getItem('trader_user') || '{}');
            storedUser.avatar = response.data.avatar;
            localStorage.setItem('trader_user', JSON.stringify(storedUser));
          }
        } catch (error) {
          toast.error(tx(error.response?.data?.detail || t('settings.avatarError')));
        } finally {
          setAvatarLoading(false);
        }
      };
      reader.onerror = () => {
        toast.error(tx(t('settings.avatarError')));
        setAvatarLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(tx(t('settings.avatarError')));
      setAvatarLoading(false);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Avatar delete handler
  const handleAvatarDelete = async () => {
    setAvatarLoading(true);
    
    try {
      const response = await traderAPI.delete('/api/trader/avatar');
      
      if (response.data.success) {
        setAvatar(null);
        toast.success(tx(t('settings.avatarDeleted')));
        
        // Update local storage
        const storedUser = JSON.parse(localStorage.getItem('trader_user') || '{}');
        delete storedUser.avatar;
        localStorage.setItem('trader_user', JSON.stringify(storedUser));
      }
    } catch (error) {
      toast.error(tx(error.response?.data?.detail || t('settings.avatarError')));
    } finally {
      setAvatarLoading(false);
    }
  };

  // Get user initials for avatar placeholder
  const getInitials = () => {
    const first = profile.first_name?.charAt(0) || '';
    const last = profile.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile'), icon: User },
    { id: 'security', label: t('settings.security'), icon: Shield },
  ];

  const inputClasses = `w-full px-4 py-3 rounded-lg border transition-colors ${
    isDark 
      ? 'bg-[#1a2332] border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
  } focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-cyan-500' : 'focus:ring-blue-500'}`;

  const labelClasses = `block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <TraderLayout>
      <div className="h-full overflow-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('nav.personalSettings')}
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('settings.subtitle')}
            </p>
          </div>

          {/* Tabs */}
          <div className={`flex gap-2 mb-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? isDark
                        ? 'border-cyan-500 text-cyan-400'
                        : 'border-blue-500 text-blue-600'
                      : isDark
                        ? 'border-transparent text-gray-400 hover:text-gray-300'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`settings-tab-${tab.id}`}
                >
                  <Icon size={18} />
                  <span className="hidden sm:inline">{tx(tab.label)}</span>
                </button>
              );
            })}
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className={`rounded-xl p-6 ${isDark ? 'bg-[#151d2b]' : 'bg-white shadow-sm border border-gray-200'}`}>
              {profileLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-cyan-500' : 'text-blue-500'}`} />
                </div>
              ) : (
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex flex-col sm:flex-row items-center gap-6 pb-6 border-b border-gray-700/50">
                    <div className="relative">
                      {/* Avatar Display */}
                      <div className={`w-24 h-24 rounded-full overflow-hidden ${
                        isDark ? 'bg-gradient-to-br from-cyan-600 to-blue-700' : 'bg-gradient-to-br from-blue-500 to-purple-600'
                      } flex items-center justify-center`}>
                        {avatarLoading ? (
                          <Loader2 className="w-8 h-8 animate-spin text-white" />
                        ) : avatar ? (
                          <img 
                            src={avatar} 
                            alt={tx("Profile")}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-bold text-white">{getInitials()}</span>
                        )}
                      </div>
                      
                      {/* Camera Button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarLoading}
                        className={`absolute bottom-0 right-0 p-2 rounded-full ${
                          isDark 
                            ? 'bg-cyan-600 hover:bg-cyan-700' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white shadow-lg transition-colors disabled:opacity-50`}
                        data-testid="avatar-upload-btn"
                      >
                        <Camera size={16} />
                      </button>
                      
                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                        data-testid="avatar-input"
                      />
                    </div>
                    
                    <div className="text-center sm:text-left">
                      <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {t('settings.profilePhoto')}
                      </h3>
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('settings.photoHint')}
                      </p>
                      <div className="flex gap-2 mt-3 justify-center sm:justify-start">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={avatarLoading}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            isDark
                              ? 'bg-gray-700 hover:bg-gray-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          } disabled:opacity-50`}
                          data-testid="avatar-change-btn"
                        >
                          {avatar ? t('settings.changePhoto') : t('settings.uploadPhoto')}
                        </button>
                        {avatar && (
                          <button
                            type="button"
                            onClick={handleAvatarDelete}
                            disabled={avatarLoading}
                            className="px-3 py-1.5 text-sm rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors disabled:opacity-50 flex items-center gap-1"
                            data-testid="avatar-delete-btn"
                          >
                            <Trash2 size={14} />
                            {t('settings.removePhoto')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <User size={20} />
                      {t('settings.personalInfo')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>{t('auth.firstName')}</label>
                        <input
                          type="text"
                          name="first_name"
                          value={profile.first_name}
                          onChange={handleProfileChange}
                          className={inputClasses}
                          data-testid="settings-first-name"
                        />
                      </div>
                      <div>
                        <label className={labelClasses}>{t('auth.lastName')}</label>
                        <input
                          type="text"
                          name="last_name"
                          value={profile.last_name}
                          onChange={handleProfileChange}
                          className={inputClasses}
                          data-testid="settings-last-name"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <Mail size={20} />
                      {t('settings.contactInfo')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>{t('auth.email')}</label>
                        <input
                          type="email"
                          name="email"
                          value={profile.email}
                          disabled
                          className={`${inputClasses} opacity-60 cursor-not-allowed`}
                          data-testid="settings-email"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {t('settings.emailCannotChange')}
                        </p>
                      </div>
                      <div>
                        <label className={labelClasses}>{t('auth.phone')}</label>
                        <div className="relative">
                          <Phone size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          <input
                            type="tel"
                            name="phone"
                            value={profile.phone}
                            onChange={handleProfileChange}
                            className={`${inputClasses} pl-10`}
                            placeholder="+90 555 123 4567"
                            data-testid="settings-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label className={labelClasses}>{t('settings.dateOfBirth')}</label>
                    <div className="relative max-w-xs">
                      <CalendarIcon size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="date"
                        name="date_of_birth"
                        value={profile.date_of_birth}
                        onChange={handleProfileChange}
                        className={`${inputClasses} pl-10`}
                        data-testid="settings-dob"
                      />
                    </div>
                  </div>

                  {/* Address Information */}
                  <div>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <MapPin size={20} />
                      {t('settings.addressInfo')}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className={labelClasses}>{t('settings.address')}</label>
                        <input
                          type="text"
                          name="address"
                          value={profile.address}
                          onChange={handleProfileChange}
                          className={inputClasses}
                          placeholder={t('settings.addressPlaceholder')}
                          data-testid="settings-address"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className={labelClasses}>{t('settings.city')}</label>
                          <input
                            type="text"
                            name="city"
                            value={profile.city}
                            onChange={handleProfileChange}
                            className={inputClasses}
                            data-testid="settings-city"
                          />
                        </div>
                        <div>
                          <label className={labelClasses}>{t('settings.country')}</label>
                          <input
                            type="text"
                            name="country"
                            value={profile.country}
                            onChange={handleProfileChange}
                            className={inputClasses}
                            data-testid="settings-country"
                          />
                        </div>
                        <div>
                          <label className={labelClasses}>{t('settings.postalCode')}</label>
                          <input
                            type="text"
                            name="postal_code"
                            value={profile.postal_code}
                            onChange={handleProfileChange}
                            className={inputClasses}
                            data-testid="settings-postal-code"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                        isDark
                          ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      data-testid="settings-save-profile"
                    >
                      {loading ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Save size={18} />
                      )}
                      {t('settings.saveChanges')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className={`rounded-xl p-6 ${isDark ? 'bg-[#151d2b]' : 'bg-white shadow-sm border border-gray-200'}`}>
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Lock size={20} />
                    {t('settings.changePassword')}
                  </h3>
                  <div className="max-w-md space-y-4">
                    {/* Current Password */}
                    <div>
                      <label className={labelClasses}>{t('settings.currentPassword')}</label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          name="current_password"
                          value={passwords.current_password}
                          onChange={handlePasswordChange}
                          className={inputClasses}
                          required
                          data-testid="settings-current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* New Password */}
                    <div>
                      <label className={labelClasses}>{t('settings.newPassword')}</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          name="new_password"
                          value={passwords.new_password}
                          onChange={handlePasswordChange}
                          className={inputClasses}
                          required
                          minLength={6}
                          data-testid="settings-new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {t('validation.passwordMin')}
                      </p>
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className={labelClasses}>{t('settings.confirmPassword')}</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirm_password"
                          value={passwords.confirm_password}
                          onChange={handlePasswordChange}
                          className={inputClasses}
                          required
                          data-testid="settings-confirm-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                      isDark
                        ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid="settings-change-password"
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Lock size={18} />
                    )}
                    {t('settings.updatePassword')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </TraderLayout>
  );
};

export default TraderSettings;
